
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Briefcase, GraduationCap, BookOpen, Presentation, Package, Globe, Truck, Mic, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const CoreyProfile = () => {
  const user = {
    name: "Corey Vickers, CSCP, ACC, CPC, ELI-MP",
    role: "iSPAI Chief Creator",
    email: "corey.vickers@ispai.io",
    solutionBounties: 10,
    domains: [
      "Supply Chain Strategy",
      "Digital Enablement",
      "Process Re-engineering",
      "Logistics Management",
      "Leadership Development",
      "Military Logistics",
      "Academic Teaching",
      "Podcast Production",
      "Strategic Planning"
    ],
    industries: [
      { name: "Military", icon: Globe },
      { name: "Oil and Gas", icon: Truck },
      { name: "Academia", icon: GraduationCap },
      { name: "Supply Chain", icon: Package },
      { name: "Consulting", icon: Briefcase },
      { name: "Media", icon: Mic }
    ],
    confirmedSolutions: 35,
    confirmedKnowledgeBases: 72,
    tokenBalance: 225,
    bio: "Corey Vickers, CSCP, ACC, CPC, ELI-MP is a Cofounder of iSPAI. He serves full time as a Senior Logistics Strategist for ExxonMobil. In addition, Corey hosts the podcast Reveille with the Veterans in Business Network. His career includes leadership roles in several major corporations, as well as serving as a Logistics Officer in the United States Air Force and adjunct professor at the University of Houston. Holding a master's degree in Logistics Management from the U.S. Air Force Institute of Technology and a bachelor's degree in biology from the United States Air Force Academy, his expertise spans supply chain strategy design, digital enablement, process re-engineering, logistics management, and various operational domains. As Chief Creator, Corey will advise on the development of iSPAI's Creation-to-Consumption Platform in coordination with iSPAI's Chief Consumer."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/a9e5eb36-05b8-4ba5-b8f6-917e1d77c481.png"
                    imageAlt="Corey Vickers"
                  >
                    <Avatar className="h-48 w-48 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/a9e5eb36-05b8-4ba5-b8f6-917e1d77c481.png" 
                        alt="Corey Vickers"
                      />
                      <AvatarFallback>CV</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-16">BIO</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <p className="text-sm leading-6">{user.bio}</p>
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" className="w-16">NW</Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <p className="text-lg font-medium text-black">{user.role}</p>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/corey"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.6} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CoreyProfile;
